import { useEffect, useState } from 'react';
import { Palette, RotateCcw, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { getPlatformTheme, updatePlatformTheme } from 'wasp/client/operations';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import {
  DEFAULT_PLATFORM_THEME,
  mergePlatformThemeConfig,
  type PlatformThemeConfig,
  type ThemeColorMode,
  type ThemeModeVariables,
} from '../../shared/theme/platformTheme';
import { hexToHslComponents, hslComponentsToHex } from '../../shared/theme/colorUtils';
import { applyPlatformTheme } from '../../client/theme/applyTheme';
import useColorMode from '../../client/hooks/useColorMode';

type ColorFieldKey = 'primary' | 'secondary' | 'destructive';

const COLOR_FIELDS: { key: ColorFieldKey; label: string }[] = [
  { key: 'primary', label: 'Primary' },
  { key: 'secondary', label: 'Secondary' },
  { key: 'destructive', label: 'Destructive' },
];

const RADIUS_OPTIONS = ['0.375rem', '0.5rem', '0.75rem', '1rem'];

function ColorTokenInput({
  label,
  hslValue,
  onChange,
}: {
  label: string;
  hslValue: string;
  onChange: (hsl: string) => void;
}) {
  const hexValue = hslComponentsToHex(hslValue);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={hexValue}
          onChange={(e) => onChange(hexToHslComponents(e.target.value))}
          className="h-10 w-14 cursor-pointer rounded border border-border bg-transparent p-1"
          aria-label={`${label} color picker`}
        />
        <Input
          value={hslValue}
          onChange={(e) => onChange(e.target.value)}
          className="font-mono text-sm"
          placeholder="221 83% 53%"
        />
      </div>
    </div>
  );
}

function ModeEditor({
  mode,
  variables,
  onChange,
}: {
  mode: ThemeColorMode;
  variables: ThemeModeVariables;
  onChange: (next: ThemeModeVariables) => void;
}) {
  const title = mode === 'light' ? 'Light mode' : 'Dark mode';

  return (
    <div className="rounded-lg border border-border bg-card p-6 space-y-5">
      <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      {COLOR_FIELDS.map(({ key, label }) => (
        <ColorTokenInput
          key={key}
          label={label}
          hslValue={variables[key] ?? DEFAULT_PLATFORM_THEME[mode][key]!}
          onChange={(hsl) => onChange({ ...variables, [key]: hsl })}
        />
      ))}
      <div className="space-y-2">
        <Label>Border radius</Label>
        <Select
          value={variables.radius ?? DEFAULT_PLATFORM_THEME[mode].radius}
          onValueChange={(radius) => onChange({ ...variables, radius })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select radius" />
          </SelectTrigger>
          <SelectContent>
            {RADIUS_OPTIONS.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

const BrandingTab = () => {
  const [colorMode] = useColorMode();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState<PlatformThemeConfig>(DEFAULT_PLATFORM_THEME);

  useEffect(() => {
    loadTheme();
  }, []);

  const loadTheme = async () => {
    try {
      setLoading(true);
      const result = await getPlatformTheme();
      setTheme(mergePlatformThemeConfig(result.theme));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to load theme';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const previewTheme = (next: PlatformThemeConfig) => {
    const mode = colorMode === 'dark' ? 'dark' : 'light';
    applyPlatformTheme(next, mode);
  };

  const handleModeChange = (mode: ThemeColorMode, variables: ThemeModeVariables) => {
    const next = { ...theme, [mode]: variables };
    setTheme(next);
    previewTheme(next);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updatePlatformTheme({ theme });
      toast.success('Platform theme saved');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to save theme';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    setTheme(DEFAULT_PLATFORM_THEME);
    previewTheme(DEFAULT_PLATFORM_THEME);
    toast.success('Reset to defaults — save to persist');
  };

  if (loading) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">Loading branding settings...</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="px-6 py-6 space-y-6">
        <Alert>
          <Palette className="h-4 w-4" />
          <AlertTitle>Platform theme (Phase 1)</AlertTitle>
          <AlertDescription>
            Colors apply via CSS variables (<code className="text-xs">--primary</code>, etc.).
            Components using semantic classes like <code className="text-xs">bg-primary</code> will
            update; hard-coded <code className="text-xs">bg-blue-600</code> styles will not.
          </AlertDescription>
        </Alert>

        <div className="grid gap-6 lg:grid-cols-2">
          <ModeEditor
            mode="light"
            variables={theme.light}
            onChange={(variables) => handleModeChange('light', variables)}
          />
          <ModeEditor
            mode="dark"
            variables={theme.dark}
            onChange={(variables) => handleModeChange('dark', variables)}
          />
        </div>

        <div className="rounded-lg border border-border bg-card p-6 space-y-4">
          <h3 className="text-lg font-semibold">Live preview</h3>
          <p className="text-sm text-muted-foreground">
            Preview reflects your current light/dark preference ({colorMode}).
          </p>
          <div className="flex flex-wrap gap-3">
            <Button>Primary button</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="outline">Outline</Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save theme'}
          </Button>
          <Button type="button" variant="outline" onClick={handleResetDefaults} disabled={saving}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to defaults
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BrandingTab;
